import { useEffect, useRef, useState, type MutableRefObject } from "react";
import type { Section, SectionEnvelope } from "../../domain/index.js";
import { useCompositionStore } from "../../stores/compositionStore.js";
import type { PlaybackEngine } from "../playback/engine.js";
import { STEM_NAMES, type StemName } from "./stage-layout.js";

/**
 * Instantaneous stage energy, per frame (Plan 13, Task 5.3). Every value is in
 * `[0,1]`. `perStem` drives each avatar; `global` (the mean of the four stems)
 * drives the crowd and the light intensity.
 */
export interface StageEnergy {
  perStem: Record<StemName, number>;
  global: number;
}

/** Energy floor — the fallback when no envelope exists or playback is idle. */
export const ZERO_ENERGY: StageEnergy = {
  perStem: { drums: 0, bass: 0, vocals: 0, other: 0 },
  global: 0,
};

/**
 * Linearly samples one stem's RMS series at `intraMs`. The series is sampled at
 * `sampleRateHz` frames per second; we interpolate between the two nearest
 * frames so the animation does not step. Out-of-range times clamp to the ends.
 */
export function sampleEnvelopeAt(
  series: readonly number[] | undefined,
  sampleRateHz: number,
  intraMs: number,
): number {
  if (!series || series.length === 0 || sampleRateHz <= 0) return 0;
  if (series.length === 1) return clamp01(series[0] ?? 0);
  const framePos = (Math.max(0, intraMs) / 1000) * sampleRateHz;
  const i0 = Math.floor(framePos);
  if (i0 >= series.length - 1) return clamp01(series[series.length - 1] ?? 0);
  const i1 = i0 + 1;
  const frac = framePos - i0;
  const a = series[i0] ?? 0;
  const b = series[i1] ?? 0;
  return clamp01(a + (b - a) * frac);
}

/** Reads the full per-stem energy of a section's envelope at `intraMs`. */
export function energyFromEnvelope(envelope: SectionEnvelope | null, intraMs: number): StageEnergy {
  if (!envelope) return ZERO_ENERGY;
  const perStem: Record<StemName, number> = { drums: 0, bass: 0, vocals: 0, other: 0 };
  for (const stem of STEM_NAMES) {
    perStem[stem] = sampleEnvelopeAt(envelope.stems[stem], envelope.sampleRateHz, intraMs);
  }
  const global = clamp01(
    (perStem.drums + perStem.bass + perStem.vocals + perStem.other) / STEM_NAMES.length,
  );
  return { perStem, global };
}

function clamp01(n: number): number {
  if (Number.isNaN(n)) return 0;
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

/**
 * Crosses live playback position with the materialized section envelopes and
 * yields per-frame stage energy. While `playing`, a `requestAnimationFrame`
 * loop reads `engine.getActiveSection()`, looks up that section's `envelope`
 * from the composition store, and samples it.
 *
 * Fallback (spec §8.1): no engine, not playing, the active section has no
 * envelope, or it is unknown → `ZERO_ENERGY`. Callers treat zero energy as
 * "animate generically / idle" — nothing crashes when the envelope job has not
 * run yet.
 */
export function useStageEnergy(engineRef: MutableRefObject<PlaybackEngine | null>): StageEnergy {
  const sections = useCompositionStore((s) => s.audioState.sections);
  const playbackState = useCompositionStore((s) => s.playback.state);
  const [energy, setEnergy] = useState<StageEnergy>(ZERO_ENERGY);

  // The latest sections array, read inside the rAF loop without re-subscribing
  // the effect every time an unrelated event hands us a fresh reference.
  const sectionsRef = useRef<Section[]>(sections);
  sectionsRef.current = sections;

  useEffect(() => {
    if (playbackState !== "playing") {
      setEnergy(ZERO_ENERGY);
      return;
    }
    let frame = 0;
    const tick = (): void => {
      const engine = engineRef.current;
      const active = engine?.getActiveSection() ?? null;
      if (!active) {
        setEnergy(ZERO_ENERGY);
      } else {
        const section = sectionsRef.current.find((s) => s.id === active.sectionId);
        setEnergy(energyFromEnvelope(section?.envelope ?? null, active.intraMs));
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playbackState, engineRef]);

  return energy;
}
